import type {
  AppBaseConfig,
  AppBaseContentModel,
  AppBaseDataSourceRole,
  DataSourceRef,
  StandardAppBaseDatabaseColumnDef,
  StandardAppBaseDatabaseRelationDef,
} from '@streamlain/view-config';
import {
  createProtectedAppBaseStandardColumnData,
  resolveAppBaseBindingDataSources,
  STANDARD_APP_BASE_DATABASE_COLUMN_DEFS,
  STANDARD_APP_BASE_DATABASE_RELATION_DEFS,
  standardAppBaseDatabaseRelationAppliesToSource,
} from '@streamlain/view-config';
import { nanoid } from 'nanoid';

type StorageColumn = {
  id: string;
  type: string;
  name: string;
  data?: unknown;
};

type StorageView = {
  columns?: Array<{ id: string; hide?: boolean; width?: number }>;
};

export interface StandardAppBaseDataSourceLike {
  storageModel?: {
    props?: {
      schemaLocked?: boolean;
      columns?: StorageColumn[];
      views?: unknown[];
    };
    store?: { transact: (callback: () => void) => void };
  };
  getAllPropertyIds(): string[];
  propertyNameGet(propertyId: string): string | undefined;
  propertyTypeGet(propertyId: string): string | undefined;
  propertyAdd(
    position: 'start' | 'end',
    property: { type: string; name: string }
  ): string | null | undefined;
  propertyDataSet(propertyId: string, data: unknown): void;
}

export interface StandardDatabaseTarget {
  dataSource: StandardAppBaseDataSourceLike;
}

type StandardRelationDataSourceRef = DataSourceRef;

type StandardRelationDataSourcesInput = {
  dataSources?: Record<string, DataSourceRef>;
  databaseBindings?: AppBaseConfig['databaseBindings'];
  productMode?: AppBaseConfig['productMode'];
};

export function ensureStandardColumns(
  dataSource: StandardAppBaseDataSourceLike,
  role: AppBaseDataSourceRole,
  contentModel?: AppBaseContentModel
): void {
  for (const column of STANDARD_APP_BASE_DATABASE_COLUMN_DEFS[role]) {
    const def = column as StandardAppBaseDatabaseColumnDef;
    const columnId =
      findPropertyIdByName(dataSource, def.name) ??
      addSchemaProperty(dataSource, {
        type: def.type,
        name: def.name,
      });
    if (columnId) {
      const publication = def.data?.publication;
      const columnData = createProtectedAppBaseStandardColumnData({
        role,
        contentModel,
        data: publication
          ? {
              ...def.data,
              publication: {
                ...publication,
                field: columnId,
                semanticKey: publication.field,
              },
            }
          : def.data,
      });
      setSchemaPropertyData(dataSource, columnId, columnData);
    }
  }
}

export function ensureStandardRelationColumns(
  appBaseConfig: StandardRelationDataSourcesInput,
  role: AppBaseDataSourceRole,
  resolve: (
    role: AppBaseDataSourceRole,
    sourceKey?: string
  ) => StandardDatabaseTarget | null,
  sourceKey?: string
): void {
  for (const spec of STANDARD_APP_BASE_DATABASE_RELATION_DEFS) {
    if (role !== spec.sourceRole && role !== spec.targetRole) continue;

    const sourceRefs = listStandardRelationSourceRefs(appBaseConfig, spec);
    const targetRefs = listDataSourceRefsByRole(appBaseConfig, spec.targetRole);
    for (const sourceRef of sourceRefs) {
      for (const targetRef of targetRefs) {
        if (
          !relationMatchesRepairRequest({
            role,
            sourceKey,
            sourceRef,
            spec,
            targetRef,
          })
        ) {
          continue;
        }
        const source = resolve(spec.sourceRole, sourceRef.key);
        const target = resolve(spec.targetRole, targetRef.key);
        if (!source || !target) continue;

        const sourceColumnId =
          findPropertyIdByName(source.dataSource, spec.sourceName) ??
          addSchemaProperty(source.dataSource, {
            type: 'relation',
            name: spec.sourceName,
          });
        const inverseColumnId =
          findPropertyIdByName(target.dataSource, spec.inverseName) ??
          addSchemaProperty(target.dataSource, {
            type: 'relation',
            name: spec.inverseName,
          });
        if (!sourceColumnId || !inverseColumnId) continue;

        if (
          readPropertyType(source.dataSource, sourceColumnId) === 'relation'
        ) {
          setSchemaPropertyData(
            source.dataSource,
            sourceColumnId,
            createProtectedAppBaseStandardColumnData({
              role: spec.sourceRole,
              contentModel: sourceRef.contentModel,
              data: {
                targetDatabaseId: targetRef.databaseId,
                targetFieldId: 'title',
                cardinality: spec.sourceCardinality,
                inverseFieldId: inverseColumnId,
                inverseCardinality: spec.inverseCardinality,
              },
            })
          );
        }
        if (
          readPropertyType(target.dataSource, inverseColumnId) === 'relation'
        ) {
          setSchemaPropertyData(
            target.dataSource,
            inverseColumnId,
            createProtectedAppBaseStandardColumnData({
              role: spec.targetRole,
              contentModel: targetRef.contentModel,
              data: {
                targetDatabaseId: sourceRef.databaseId,
                targetFieldId: 'title',
                cardinality: spec.inverseCardinality,
                inverseFieldId: sourceColumnId,
                inverseCardinality: spec.sourceCardinality,
              },
            })
          );
        }
      }
    }
  }
}

export function standardRelationAppliesToSource(
  spec: StandardAppBaseDatabaseRelationDef,
  source: Pick<DataSourceRef, 'contentModel' | 'role'>
): boolean {
  return standardAppBaseDatabaseRelationAppliesToSource(spec, source);
}

export function listStandardRelationSourceRefs(
  appBaseConfig: StandardRelationDataSourcesInput,
  spec: StandardAppBaseDatabaseRelationDef
): StandardRelationDataSourceRef[] {
  return listDataSourceRefsByRole(appBaseConfig, spec.sourceRole).filter(
    source => standardRelationAppliesToSource(spec, source)
  );
}

function listDataSourceRefsByRole(
  appBaseConfig: StandardRelationDataSourcesInput,
  role: AppBaseDataSourceRole
): StandardRelationDataSourceRef[] {
  return Object.values(getAppBaseDataSources(appBaseConfig)).filter(
    (source): source is StandardRelationDataSourceRef => source.role === role
  );
}

export function getAppBaseDataSources(
  appBaseConfig: StandardRelationDataSourcesInput
): Record<string, DataSourceRef> {
  if (appBaseConfig.dataSources) return appBaseConfig.dataSources;
  if (!appBaseConfig.databaseBindings) return {};
  return resolveAppBaseBindingDataSources(appBaseConfig as AppBaseConfig);
}

function relationMatchesRepairRequest(input: {
  role: AppBaseDataSourceRole;
  sourceKey: string | undefined;
  sourceRef: StandardRelationDataSourceRef;
  spec: StandardAppBaseDatabaseRelationDef;
  targetRef: StandardRelationDataSourceRef;
}): boolean {
  if (input.role === input.spec.sourceRole) {
    return !input.sourceKey || input.sourceRef.key === input.sourceKey;
  }
  if (input.role === input.spec.targetRole) {
    return !input.sourceKey || input.targetRef.key === input.sourceKey;
  }
  return false;
}

function findPropertyIdByName(
  dataSource: StandardAppBaseDataSourceLike,
  name: string
): string | null {
  return (
    dataSource
      .getAllPropertyIds()
      .find(propertyId => dataSource.propertyNameGet(propertyId) === name) ??
    null
  );
}

function addSchemaProperty(
  dataSource: StandardAppBaseDataSourceLike,
  property: { type: string; name: string }
): string | null | undefined {
  const propertyId = withSchemaUnlocked(dataSource, () =>
    dataSource.propertyAdd('end', property)
  );
  if (propertyId) return propertyId;
  return withSchemaUnlocked(dataSource, () =>
    mutateStorageModel(dataSource, () =>
      appendStorageProperty(dataSource, property)
    )
  );
}

function setSchemaPropertyData(
  dataSource: StandardAppBaseDataSourceLike,
  propertyId: string,
  data: unknown
): void {
  withSchemaUnlocked(dataSource, () => {
    dataSource.propertyDataSet(propertyId, data);
  });
  replaceStorageColumnData(dataSource, propertyId, data);
}

function appendStorageProperty(
  dataSource: StandardAppBaseDataSourceLike,
  property: { type: string; name: string }
): string | null {
  const props = dataSource.storageModel?.props;
  if (!props) return null;
  props.columns ??= [];
  const existing = props.columns.find(column => column.name === property.name);
  if (existing) return existing.id;
  const id = nanoid();
  props.columns.push({
    id,
    type: property.type,
    name: property.name,
    data: {},
  });
  for (const view of props.views ?? []) {
    const storageView = view as StorageView;
    storageView.columns ??= [];
    if (storageView.columns.some(column => column.id === id)) continue;
    storageView.columns.push({ id, hide: false, width: 180 });
  }
  return id;
}

function readPropertyType(
  dataSource: StandardAppBaseDataSourceLike,
  propertyId: string
): string | undefined {
  return (
    dataSource.propertyTypeGet(propertyId) ??
    findStorageColumn(dataSource, propertyId)?.type
  );
}

function findStorageColumn(
  dataSource: StandardAppBaseDataSourceLike,
  propertyId: string
): StorageColumn | undefined {
  return dataSource.storageModel?.props?.columns?.find(
    column => column.id === propertyId
  );
}

function replaceStorageColumnData(
  dataSource: StandardAppBaseDataSourceLike,
  propertyId: string,
  data: unknown
): void {
  mutateStorageModel(dataSource, () => {
    const columns = dataSource.storageModel?.props?.columns;
    if (!columns) return;
    const index = columns.findIndex(column => column.id === propertyId);
    const column = columns[index];
    if (!column) return;
    columns[index] = { ...column, data: structuredClone(data) };
  });
}

function mutateStorageModel<T>(
  dataSource: StandardAppBaseDataSourceLike,
  callback: () => T
): T {
  const store = dataSource.storageModel?.store;
  if (!store?.transact) return callback();
  let result!: T;
  store.transact(() => {
    result = callback();
  });
  return result;
}

function withSchemaUnlocked<T>(
  dataSource: StandardAppBaseDataSourceLike,
  callback: () => T
): T {
  const props = dataSource.storageModel?.props;
  if (!props?.schemaLocked) return callback();
  let result!: T;
  const run = () => {
    props.schemaLocked = false;
    try {
      result = callback();
    } finally {
      props.schemaLocked = true;
    }
  };
  run();
  return result;
}
